import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Circle,
  CircleCheck,
  Diamond,
  HeartHandshake,
  Loader2,
  Lock,
  PenLine,
  Quote,
  Sun,
  Trash2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  ApiError,
  acknowledgeSponseeStep,
  getSponseeStep,
  saveSponseeStepNote,
  type SponseeStepWork,
} from "@/lib/api";
import { shortDate } from "@/lib/format";

/**
 * A sponsee's step, as their sponsor sees it: strictly read-only. Reflections
 * appear only when the sponsee chose "share my reflections"; otherwise this is
 * the hand-off itself — status and a nudge. Acknowledging marks the step
 * worked together (and quietly opens the sponsee's next step).
 */
export default function SponseeStepView() {
  const navigate = useNavigate();
  const { ownerId = "", n = "1" } = useParams();
  const stepNumber = Number(n) || 1;

  const [data, setData] = useState<SponseeStepWork | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sittingNote, setSittingNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);

  async function saveNote() {
    setBusy(true);
    try {
      await saveSponseeStepNote(ownerId, stepNumber, sittingNote.trim());
      setNoteSaved(true);
    } catch {
      setError("Couldn't save your note.");
    } finally {
      setBusy(false);
    }
  }

  async function load() {
    try {
      setData(await getSponseeStep(ownerId, stepNumber));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else if (e instanceof ApiError && e.status === 403) setError("This isn't shared with you.");
      else setError("Couldn't load this step.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, stepNumber]);

  async function acknowledge() {
    setBusy(true);
    try {
      await acknowledgeSponseeStep(ownerId, stepNumber, sittingNote.trim() || undefined);
      setConfirming(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't mark this step worked.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Not available."}</p>
        <Button variant="outline" onClick={() => navigate(`/sponsor/${ownerId}`)}>
          Back
        </Button>
      </div>
    );
  }

  const shared = data.is_shared === 1;
  const title = `${data.name}${data.principle ? ` — ${data.principle}` : ""}`;

  /* ---- Step 5: the in-person acknowledgement — read-only for the sponsor ---- */
  if (data.step_number === 5) {
    const s5done = data.status === "worked";
    const timeline: [string, string, boolean][] = [
      ["Step 4 marked ready", data.s4_ready_at ? shortDate(data.s4_ready_at) : "Not yet", !!data.s4_ready_at],
      [
        "Read aloud to you",
        s5done ? `${shortDate(data.worked_at ?? data.updated_at)} · marked by them` : "Not yet",
        s5done,
      ],
      [
        data.s4_wiped_at ? "Step 4 writing deleted" : "Step 4 writing kept",
        data.s4_wiped_at ? `${shortDate(data.s4_wiped_at)} · by them` : "Theirs to decide",
        !!data.s4_wiped_at,
      ],
    ];
    return (
      <div className="pb-16">
        <div className="flex items-center gap-2 px-4 pb-2 pt-6">
          <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(`/sponsor/${ownerId}`)}>
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </Button>
          <h1 className="flex-1 text-lg font-semibold text-foreground">Step 5</h1>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3 w-3" strokeWidth={2} /> In person
          </span>
        </div>
        <div className="flex flex-col gap-3.5 px-6 pt-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              {data.name} · Step Five
            </p>
            <h2 className="mt-1 text-xl font-bold text-foreground">
              {s5done
                ? `Read to you ${shortDate(data.worked_at ?? data.updated_at)}.`
                : "When the two of you sit down."}
            </h2>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {s5done
              ? "They marked this after your sitting. The app holds the date and nothing else."
              : "They read their Step 4 to you, out loud, in person. Happy24 doesn't schedule it, host it, or record any of it."}
          </p>
          <Card>
            <CardContent className="flex flex-col gap-3 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Timeline
              </p>
              {timeline.map(([t, d, on]) => (
                <div key={t} className={`flex items-center gap-3 ${on ? "" : "opacity-55"}`}>
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                      on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {on ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <Circle className="h-3 w-3" strokeWidth={2} />}
                  </span>
                  <div className="flex-1">
                    <p className="text-[13.5px] font-semibold text-foreground">{t}</p>
                    <p className="text-xs text-muted-foreground">{d}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="bg-secondary">
            <CardContent className="flex items-start gap-2.5 px-4 py-3.5">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
              <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                {s5done
                  ? "You were there, so Happy24 didn't ask you to confirm it as well. One acknowledgement is enough."
                  : "Nothing for you to do in the app. When it's happened, they mark it themselves."}
              </p>
            </CardContent>
          </Card>
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Your private note
            </p>
            <p className="text-xs text-muted-foreground">Only you can see this. They never do.</p>
            <Textarea
              rows={3}
              placeholder="Anything you want to remember for next time."
              value={sittingNote || data.my_note?.note_text || ""}
              onChange={(e) => setSittingNote(e.target.value)}
            />
          </div>
          <Button
            size="xl"
            fullWidth
            disabled={busy || !sittingNote.trim()}
            onClick={() => void saveNote()}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : noteSaved ? "Saved" : "Save Note"}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => navigate(`/sponsor/${ownerId}`)}>
            Back to {data.name}
          </Button>
        </div>
      </div>
    );
  }

  /* ------------------- Step 4: status only, always (§Step 4) ------------------- */
  if (data.step_number === 4) {
    const ready = data.status === "ready";
    const worked = data.status === "worked";
    const daysQuiet = data.last_activity
      ? Math.floor((Date.now() - new Date(data.last_activity).getTime()) / 86_400_000)
      : 0;
    const quiet = !ready && !worked && daysQuiet >= 12;
    const timeline: [typeof Check, string, string | null][] = [
      [Check, "Opened Step 4", data.started_at ? shortDate(data.started_at) : null],
      [
        PenLine,
        quiet ? "Last worked on it" : "Worked on it most recently",
        data.last_activity ? `${shortDate(data.last_activity)}${quiet ? ` · ${daysQuiet} days ago` : ""}` : null,
      ],
      ...(ready || worked
        ? ([[CircleCheck, "Marked ready to read it to you", shortDate(data.updated_at)]] as [typeof Check, string, string | null][])
        : []),
      ...(worked
        ? ([[HeartHandshake, "Read with you · you marked it worked", data.acknowledged_at ? shortDate(data.acknowledged_at) : null]] as [typeof Check, string, string | null][])
        : []),
      ...(data.wiped_at
        ? ([[Trash2, "They deleted their writing — the step stays worked", shortDate(data.wiped_at)]] as [typeof Check, string, string | null][])
        : []),
    ];
    return (
      <div className="pb-16">
        <div className="flex items-center gap-2 px-4 pb-2 pt-6">
          <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(`/sponsor/${ownerId}`)}>
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </Button>
          <h1 className="flex-1 text-lg font-semibold text-foreground">{title}</h1>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" strokeWidth={2} /> Status only
          </span>
        </div>
        <div className="flex flex-col gap-3 px-6 pt-2">
          <h2 className="text-xl font-bold text-foreground">
            {worked
              ? `Worked together${data.acknowledged_at ? ` ${shortDate(data.acknowledged_at)}` : ""}.`
              : ready
                ? "They're ready to read it to you."
                : quiet
                  ? "Still in it. It's been a couple of weeks."
                  : "They're writing their inventory."}
          </h2>
          <Card className="bg-secondary">
            <CardContent className="flex items-start gap-2.5 px-4 py-3.5">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
              <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                Unlike the other steps, Step 4 has no sharing option. Their inventory is encrypted
                on their phone and can't be read by anyone else — including us. You'll hear it from
                them, out loud, in Step 5. What you can see here is that they're doing the work,
                and when.
              </p>
            </CardContent>
          </Card>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              What you can see
            </p>
            <div className="mt-2 flex flex-col gap-2.5">
              {timeline.map(([Icon, label, when]) =>
                when ? (
                  <div key={label} className="flex items-start gap-2.5">
                    <Icon className="mt-0.5 h-[15px] w-[15px] shrink-0 text-primary" strokeWidth={2} />
                    <div>
                      <p className="text-[13.5px] text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{when}</p>
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
          {quiet && (
            <div className="flex items-start gap-3 rounded-lg border-2 border-status-yellow bg-status-yellow-bg px-4 py-3.5">
              <Diamond className="mt-0.5 h-4 w-4 shrink-0 text-status-yellow-text" strokeWidth={2} />
              <p className="text-[13px] leading-relaxed text-foreground">
                This is where people most often stall. Nothing is wrong — it might just be a good
                week to ask how it's going, without asking what's in it.
              </p>
            </div>
          )}
          {ready && !confirming && (
            <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
              <Sun className="mt-0.5 h-[17px] w-[17px] shrink-0 text-primary" strokeWidth={2} />
              <p className="text-[13px] leading-relaxed text-foreground">
                Step 5 is a sitting, not a screen. Find a couple of unhurried hours somewhere
                private. They read; you listen.
              </p>
            </div>
          )}
          {worked && !data.wiped_at && (
            <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
              <Trash2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
              <p className="text-[13px] leading-relaxed text-foreground">
                They can delete their Step 4 writing whenever they want now. If they do, this step
                stays marked worked — nothing you see here changes.
              </p>
            </div>
          )}
          {data.my_note && (
            <Card>
              <CardContent className="px-4 py-3.5">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Your own note · private to you
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                  {data.my_note.note_text}
                </p>
              </CardContent>
            </Card>
          )}
          {ready &&
            (confirming ? (
              <Card>
                <CardContent className="flex flex-col gap-3 px-4 py-3.5">
                  <p className="text-sm font-semibold text-foreground">
                    Did they read their Step 4 to you?
                  </p>
                  <p className="text-[13px] leading-relaxed text-muted-foreground">
                    Marking it worked is a plain record between the two of you. It doesn't grade
                    anything, and it doesn't unlock anything for you to read.
                  </p>
                  <div>
                    <label className="mb-0.5 block text-[13px] font-semibold text-foreground">
                      Your own note <span className="font-normal text-muted-foreground">(optional)</span>
                    </label>
                    <p className="mb-1.5 text-xs text-muted-foreground">
                      Private to you. They never see this, and it isn't part of their inventory.
                    </p>
                    <Textarea
                      rows={4}
                      placeholder="What you want to remember about the conversation…"
                      value={sittingNote}
                      onChange={(e) => setSittingNote(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button disabled={busy} onClick={acknowledge}>
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                      ) : (
                        "Mark Step 4 Worked"
                      )}
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                      Not yet
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button variant="outline" fullWidth onClick={() => setConfirming(true)}>
                <Check className="h-4 w-4" strokeWidth={2} />
                We sat down and they read it
              </Button>
            ))}
          <p className="text-center text-xs text-muted-foreground">
            Step 4 writing is never shared through Happy24 — not with you, not with us.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back"
          onClick={() => navigate(`/sponsor/${ownerId}`)}
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">{title}</h1>
        <Badge status={data.status === "worked" ? "green" : "yellow"}>
          {data.status === "worked" ? "Worked" : "Ready"}
        </Badge>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        <Card className="bg-secondary">
          <CardContent className="flex items-start gap-2.5 px-4 py-3">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              Read-only.{" "}
              {data.step_number === 8 || data.step_number === 9
                ? `Step ${data.step_number} is never shared in the app — by design, not by their choice. The list names other people, and it's encrypted to their phone.`
                : shared
                  ? "They chose to share this step's writing with you."
                  : "They've said they're ready to talk — their writing stays private."}
            </p>
          </CardContent>
        </Card>

        {data.ready_to_talk === 1 && data.status === "ready" && (
          <Card>
            <CardContent className="flex items-start gap-3 px-4 py-3.5">
              <HeartHandshake className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
              <p className="text-sm leading-relaxed text-foreground">
                They're ready to talk this step through when it suits you both. No
                deadline — a door opening, not an alarm.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Steps 8 and 9 (canvas §6): there is nothing to read, and the screen
            says so plainly rather than showing an empty state that reads as
            withholding. Step 9 adds that some amends are deliberately not made. */}
        {(data.step_number === 8 || data.step_number === 9) && (
          <>
            <Card>
              <CardContent className="flex flex-col gap-2 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Nothing to read
                </p>
                <p className="text-sm leading-relaxed text-foreground">
                  {data.step_number === 9
                    ? data.status === "ready" || data.status === "worked"
                      ? "They've worked Step 9 and are living it — that's the whole signal you get here. Amends keep happening after this."
                      : "They're working through their amends. Nothing to open; you plan them together, one at a time."
                    : data.status === "ready" || data.status === "worked"
                      ? "They've made their list and are willing to start — that's the whole signal you get here."
                      : "They're building their list. Ask them how it's coming when you talk; there's nothing to open."}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-secondary">
              <CardContent className="flex items-start gap-2.5 px-4 py-3">
                <HeartHandshake className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                  {data.step_number === 9
                    ? "Plan each amends with them the way you always have. Some will take years, and some they'll decide against — both are the step being worked."
                    : "Go through the list with them the way you always have: out loud, one person at a time, with the ones they aren't willing about given the same room as the rest."}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-secondary">
              <CardContent className="flex items-start gap-2.5 px-4 py-3">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                  {data.step_number === 9
                    ? "The app never assesses whether an amends would cause harm, and it never contacts anyone. Nothing about their list is visible to you, including how much of it is unfinished."
                    : "Some rows will be marked to talk through — they'll raise those. The app doesn't decide that an amends would cause harm, and it doesn't tell you which ones they flagged."}
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {shared && (
          <>
            <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Their reflections
            </p>
            {data.reflections.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing written yet.</p>
            )}
            {data.reflections.map((r) => (
              <Card key={r.id}>
                <CardContent className="flex flex-col gap-2 px-4 py-3.5">
                  <p className="text-sm font-medium text-foreground">{r.prompt_text}</p>
                  <div className="flex gap-2">
                    <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2} />
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                      {r.answer_text}
                    </p>
                  </div>
                  {r.additions.map((a, i) => (
                    <p
                      key={i}
                      className="ml-5 border-l-2 border-border pl-3 text-sm leading-relaxed text-muted-foreground"
                    >
                      {a.added_text}
                    </p>
                  ))}
                </CardContent>
              </Card>
            ))}
            {data.willingness_note && (
              <Card>
                <CardContent className="px-4 py-3.5">
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    {/* The step's own word for its fourth phase — "The asking"
                        at Step 7 (Step 7 canvas). */}
                    {data.willingness_label ?? "Willingness"}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground">
                    {data.willingness_note}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {data.status === "worked" ? (
          <Card className="border-primary">
            <CardContent className="flex items-start gap-2.5 px-4 py-3.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
              <p className="text-sm leading-relaxed text-foreground">
                Worked together{data.acknowledged_at ? ` · ${shortDate(data.acknowledged_at)}` : ""}.
                Their next step is open whenever they're ready.
              </p>
            </CardContent>
          </Card>
        ) : confirming ? (
          <Card>
            <CardContent className="flex flex-col gap-3 px-4 py-3.5">
              <p className="text-sm leading-relaxed text-foreground">
                Mark {data.name} as worked together? This quietly opens their next step.
              </p>
              <div className="flex gap-2">
                <Button disabled={busy} onClick={acknowledge}>
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                  ) : (
                    "Yes — worked together"
                  )}
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
                  Not yet
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button variant="outline" fullWidth onClick={() => setConfirming(true)}>
            <Check className="h-4 w-4" strokeWidth={2} />
            We've worked this step together
          </Button>
        )}
        <p className="text-center text-xs text-muted-foreground">
          Encouragement, not evaluation. One day at a time.
        </p>
      </div>
    </div>
  );
}
